import { todayIn } from "../calendar/zoned-time";
import type {
  LeaveBalance,
  RotaDay,
  RotaWeek,
  StaffMember,
  TeamCard,
  TimeOffRequest,
} from "@dentacore/shared-types";

import { KIND_LABELS } from "./weekdays";

/** One person as the Staff page draws them: who, their week here, and their figures. */
export interface Person {
  id: string;
  name: string;
  initials: string;
  role: string;
  /** False for a leaver or somebody whose invite is still outstanding — never scheduled. */
  active: boolean;
  /** Their dated week at this location; null when they do not work here. */
  week: RotaWeek | null;
  /** The other locations they belong to, by name. */
  elsewhere: string[];
  /**
   * Every location they belong to, this one first (EB3) — what the schedule
   * names under them when the clinic has more than one.
   */
  locations: string[];
  card: TeamCard | null;
  balance: LeaveBalance | null;
}

/**
 * Everybody on the clinic's staff, joined to what the location knows of them.
 *
 * **The week decides "works here"**: the server returns one for every active
 * member of this location, whether or not their hours were ever set, so a
 * person without one is somewhere else — or cannot be scheduled at all.
 */
export function buildPeople(
  staff: readonly StaffMember[],
  weeks: readonly RotaWeek[] | null,
  team: readonly TeamCard[] | null,
  balances: readonly LeaveBalance[] | null,
  branchId: string | null,
): Person[] {
  const weekOf = new Map((weeks ?? []).map((week) => [week.userId, week]));
  const cardOf = new Map((team ?? []).map((card) => [card.userId, card]));
  const balanceOf = new Map((balances ?? []).map((row) => [row.userId, row]));

  return staff.map((member) => ({
    id: member.id,
    name: `${member.firstName} ${member.lastName}`,
    initials: initialsOf(member.firstName, member.lastName),
    role: member.role,
    active: member.isActive,
    week: weekOf.get(member.id) ?? null,
    elsewhere: member.memberships
      .filter((row) => row.branchId !== branchId)
      .map((row) => row.branchName ?? "another location"),
    locations: [
      ...member.memberships.filter((row) => row.branchId === branchId),
      ...member.memberships.filter((row) => row.branchId !== branchId),
    ].map((row) => row.branchName ?? "another location"),
    card: cardOf.get(member.id) ?? null,
    balance: balanceOf.get(member.id) ?? null,
  }));
}

/** This person's day on a date, or null when their week is not here or not visible. */
export function dayFor(person: Person, date: string): RotaDay | null {
  return person.week?.days.find((day) => day.date === date) ?? null;
}

/**
 * The rules the Staff page draws from, kept out of the components so they can
 * be tested without a browser (ST9–ST14).
 *
 * **None of this computes a rota.** Who works when, what a week adds up to and
 * what leave costs are the server's (§6.4); these only decide how a day that
 * already came back is described and where its bar sits on the card.
 */

export type DayState = "work" | "half" | "training" | "off" | "leave";

/** What a person's day is, leave first — approved leave outranks the rota. */
export function dayStateOf(day: RotaDay, onLeave: boolean): DayState {
  if (onLeave) return "leave";
  if (day.kind === "OFF") return "off";
  if (day.kind === "HALF_DAY") return "half";
  if (day.kind === "TRAINING") return "training";
  return "work";
}

/** Whether somebody with this day counts as in. */
export function isIn(state: DayState): boolean {
  return state === "work" || state === "half" || state === "training";
}

/** Whether an approved request covers this person on this date. */
export function onLeaveOn(
  userId: string,
  date: string,
  approved: readonly TimeOffRequest[],
): boolean {
  return approved.some(
    (row) => row.userId === userId && row.startsOn <= date && date <= row.endsOn,
  );
}

/** `"09:30"` → `570`. */
export function minutesOf(clock: string): number {
  const [hours, minutes] = clock.split(":").map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/** `2100` → `35h`, `2130` → `35h 30m`. Hours are how a rota is read. */
export function hoursOf(minutes: number): string {
  const sign = minutes < 0 ? "−" : "";
  const whole = Math.abs(minutes);
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${sign}${rest === 0 ? `${String(hours)}h` : `${String(hours)}h ${String(rest)}m`}`;
}

/** `2.5` → `2½`, `-1` → `−1`, `0.5` → `½`. Leave comes in half days. */
export function daysText(days: number): string {
  const whole = Math.floor(Math.abs(days));
  const half = Math.abs(days) - whole >= 0.5;
  const text = half ? (whole === 0 ? "½" : `${String(whole)}½`) : String(whole);
  return `${days < 0 ? "−" : ""}${text}`;
}

/**
 * Where a day's timeline starts and ends, in whole hours.
 *
 * **From the shifts on it, never from a constant**: the earliest start rounded
 * down and the latest end rounded up, so the bars use the card's width. At
 * least eight hours wide, so a day with one short shift does not draw it
 * across the whole card; never past midnight. Null when nobody has hours.
 */
export function spanOf(
  shifts: readonly { startsAt: string | null; endsAt: string | null }[],
): { from: number; to: number } | null {
  const timed = shifts.filter(
    (shift): shift is { startsAt: string; endsAt: string } =>
      shift.startsAt !== null && shift.endsAt !== null,
  );
  if (timed.length === 0) return null;

  const earliest = Math.min(...timed.map((shift) => minutesOf(shift.startsAt)));
  const latest = Math.max(...timed.map((shift) => minutesOf(shift.endsAt)));
  let from = Math.floor(earliest / 60);
  let to = Math.ceil(latest / 60);
  if (to - from < MIN_SPAN) to = from + MIN_SPAN;
  if (to > 24) {
    from = Math.max(0, from - (to - 24));
    to = 24;
  }
  return { from, to };
}

/** The narrowest day the timeline draws, in hours — a drawing rule, not opening hours. */
const MIN_SPAN = 8;

/** How far along the span a clock time sits, 0 to 1. */
export function fractionOf(clock: string, span: { from: number; to: number }): number {
  const at = (minutesOf(clock) - span.from * 60) / ((span.to - span.from) * 60);
  return Math.min(1, Math.max(0, at));
}

/** `Clinic 09:00–17:00` reads as `09:00–17:00`; the other kinds keep their word. */
export function shiftText(day: {
  kind: string;
  startsAt: string | null;
  endsAt: string | null;
}): string {
  if (day.kind === "OFF") return "Off";
  const times = `${day.startsAt ?? "—"}–${day.endsAt ?? "—"}`;
  return day.kind === "CLINIC" ? times : `${KIND_LABELS[day.kind] ?? ""} ${times}`;
}

/** The ISO date `days` after this one. */
export function addDays(iso: string, days: number): string {
  const at = new Date(`${iso}T00:00:00.000Z`);
  return new Date(at.getTime() + days * 86_400_000).toISOString().slice(0, 10);
}

/** The seven dates of the week starting on this Monday. */
export function datesOf(monday: string): string[] {
  return Array.from({ length: 7 }, (_, offset) => addDays(monday, offset));
}

/** Today in the browser's own calendar, as `YYYY-MM-DD` — the day a person means by today. */
export function localToday(timeZone: string, now = new Date()): string {
  // The location's day, not the browser's (P2 Q8, QA third run).
  return todayIn(timeZone, now);
}

/** `2026-09-28` → `Monday 28 September`. */
export function longDate(iso: string): string {
  const at = new Date(`${iso}T00:00:00.000Z`);
  return `${WEEKDAYS[at.getUTCDay()] ?? ""} ${String(at.getUTCDate())} ${
    MONTHS_LONG[at.getUTCMonth()] ?? ""
  }`;
}

/** `2026-09-09` → `9 Sep`. */
export function dayAndMonth(iso: string): string {
  const at = new Date(`${iso}T00:00:00.000Z`);
  return `${String(at.getUTCDate())} ${MONTHS[at.getUTCMonth()] ?? ""}`;
}

/** `FRONT_DESK` → `Front desk`. */
export function roleLabel(role: string): string {
  const words = role.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The first letter of each name, for the round badge. */
export function initialsOf(firstName: string, lastName: string): string {
  return `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase();
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
